import { Permission, UserType } from '@/models/user';
import { useAuthentication } from '@/hooks/useAuthentication';
import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { getPathnamesOfItems } from '@starjack/layout';
import { useB2bSidebarItems } from '@/layouts/sidebar/config';

const comparePermissions = (
    permissionsHolding: Permission[] = [],
    permissionsRequired: Permission[] = []
) => {
    if (!permissionsRequired.length) return true;
    if (!permissionsHolding.length && permissionsRequired.length) return false;

    return permissionsRequired.some((permission) => {
        return permissionsHolding.includes(permission);
    });
};

type UserIsAuthorized = [
    isAuthorized: boolean,
    options: {
        checkPermissions: (permissionsNeeded: Permission[]) => boolean;
    },
];

export const useIsAuthorized = (
    permissionsRequired: Permission[]
): UserIsAuthorized => {
    const location = useLocation();
    const pathname = location.pathname;
    const { permissions, userType } = useAuthentication();
    const b2bSidebarItems = useB2bSidebarItems();
    const isAuthorized = useMemo(() => {
        if (userType === UserType.LiftOperator) {
            return true;
        }

        // b2b only allow
        if (userType === UserType.B2B) {
            const b2bPaths = getPathnamesOfItems(
                b2bSidebarItems as unknown as import('@starjack/layout').SidebarEntry[]
            );
            return b2bPaths.includes(pathname);
        }

        return comparePermissions(permissions, permissionsRequired);
    }, [userType, permissions, permissionsRequired, b2bSidebarItems, pathname]);

    const checkPermissions = useCallback(
        (permissionsNeeded: Permission[]) => {
            return comparePermissions(permissions, permissionsNeeded);
        },
        [permissions]
    );

    return [
        isAuthorized,
        {
            checkPermissions,
        },
    ];
};
